import { windowLocalStorageSsr } from './windowStoragesSsr'

/**
 * Device memory for the addresses a shopper types into checkout, so a returning
 * shopper does not retype what they used last time.
 *
 * This layer is deliberately dumb: it namespaces, stamps an expiry and survives a
 * corrupt or full store. It knows nothing about precedence or about when the opt-in
 * should be asked for — both live in `useRememberedAddress`, which is the only thing
 * that should call these functions.
 *
 * Remembered data is a *convenience*. It may only fill a gap the order and the
 * customer's account have both left empty, and it is never auto-submitted.
 *
 * Nothing here is written unless the shopper has ticked "remember my details on this
 * device". Storing a name, street, email and phone on someone's machine for months is
 * not strictly necessary to sell them anything, so ePrivacy Art. 5(3) — which covers
 * `localStorage`, not just cookies — needs the storage to be something they explicitly
 * asked for. The opt-in flag itself is the record of that request.
 */

/**
 * The four forms that remember separately. One blob would be wrong: a PostNord
 * recipient is postcode-led and a billing address is contact-led, they are filled at
 * different moments, and clearing one must not clear the others.
 */
export type RememberedAddressKind = 'billing' | 'shipping' | 'postnord' | 'nshift'

/** Identity bucket used for everyone who is not signed in. */
export const GUEST_IDENTITY = 'guest'

/**
 * The shopper's "remember my details on this device" choice. Device-wide rather than
 * per-form: a checkout renders two or three of these forms at once, and asking the same
 * question three times with the same wording is not informed consent, it is noise.
 */
const OPT_IN_SLOT = 'opt-in'

type StorageSlot = RememberedAddressKind | typeof OPT_IN_SLOT

/**
 * Bumped on a breaking shape change. A mismatch drops the entry rather than migrating —
 * there is no migration path worth writing for a convenience, and a wrongly-shaped
 * address is worse than none.
 */
const VERSION = 1
const NAMESPACE = 'checkout-address'
/** Records which identity the buckets on this device were last written for. */
const IDENTITY_MARKER_KEY = `${NAMESPACE}:identity`

/**
 * 180 days. The cart expires in an hour because a cart is volatile; an address is not.
 * Much longer than this and we start prefilling an address the shopper has moved away
 * from, which is the failure mode that ships a parcel to the wrong place.
 */
const LIFETIME_MS = 180 * 24 * 60 * 60 * 1000

interface RememberedEntry<T> {
  version: number
  /** Epoch ms. Past it the entry is deleted on read, the same rule the cart follows. */
  expiresAt: number
  value: T
}

// `checkout-address:v1:<identity>:<slot>` — identity second so an entry's owner can be
// read straight off the key when purging.
const buildKey = (identity: string, slot: StorageSlot) =>
  `${NAMESPACE}:v${VERSION}:${identity}:${slot}`

const identityOfKey = (key: string) => key.split(':')[2]
const versionOfKey = (key: string) => key.split(':')[1]

/**
 * The cart stores `btoa(JSON.stringify(...))`, which throws on any character outside
 * Latin-1 — fine for a cart of product ids, not for an address with a Polish or Cyrillic
 * street name. Percent-encoding first keeps the input ASCII so `btoa` always succeeds.
 */
const encode = (value: unknown) => btoa(encodeURIComponent(JSON.stringify(value)))
const decode = <T>(raw: string): T => JSON.parse(decodeURIComponent(atob(raw))) as T

/** Every remembered-address bucket on this device, across identities and versions. */
const listBucketKeys = (): string[] => {
  const keys: string[] = []

  for (let i = 0; i < windowLocalStorageSsr.length; i += 1) {
    const key = windowLocalStorageSsr.key(i)
    if (key?.startsWith(`${NAMESPACE}:v`)) {
      keys.push(key)
    }
  }

  return keys
}

const readEntry = <T>(identity: string, slot: StorageSlot): T | null => {
  try {
    const raw = windowLocalStorageSsr.getItem(buildKey(identity, slot))

    if (raw === null) {
      return null
    }

    const entry = decode<RememberedEntry<T> | null>(raw)

    if (!entry || entry.version !== VERSION || Date.now() > entry.expiresAt) {
      windowLocalStorageSsr.removeItem(buildKey(identity, slot))
      return null
    }

    return entry.value
  } catch (_e) {
    return null
  }
}

const writeEntry = <T>(identity: string, slot: StorageSlot, value: T): void => {
  try {
    const entry: RememberedEntry<T> = {
      version: VERSION,
      expiresAt: Date.now() + LIFETIME_MS,
      value
    }

    windowLocalStorageSsr.setItem(buildKey(identity, slot), encode(entry))
  } catch (_e) {
    void 0
  }
}

/**
 * Read the address remembered for this identity and form, or `null` when there is none,
 * it has expired, it was written by an older schema, or the store is unreadable.
 */
export const readRememberedAddress = <T>(identity: string, kind: RememberedAddressKind): T | null =>
  readEntry<T>(identity, kind)

/** Remember an address for this identity and form. A full or disabled store is a no-op. */
export const writeRememberedAddress = <T>(
  identity: string,
  kind: RememberedAddressKind,
  value: T
): void => writeEntry(identity, kind, value)

/** Drop one form's memory for one identity, leaving the other three alone. */
export const forgetRememberedAddress = (identity: string, kind: RememberedAddressKind): void => {
  try {
    windowLocalStorageSsr.removeItem(buildKey(identity, kind))
  } catch (_e) {
    void 0
  }
}

/**
 * Whether this identity asked us to remember their details on this device.
 *
 * A missing flag means no. The flag carries the same expiry as the addresses it governs,
 * so the permission lapses at the same moment the data does rather than standing
 * indefinitely.
 */
export const readRememberedAddressOptIn = (identity: string): boolean =>
  readEntry<boolean>(identity, OPT_IN_SLOT) === true

/** Record the shopper's opt-in. Only ever called from an explicit tick of the box. */
export const writeRememberedAddressOptIn = (identity: string): void =>
  writeEntry(identity, OPT_IN_SLOT, true)

/**
 * Withdraw the opt-in and drop everything it covered, for this identity only.
 *
 * Unticking the box is a withdrawal, and a withdrawal has to remove the data — not
 * merely stop it being read.
 */
export const forgetRememberedIdentity = (identity: string): void => {
  try {
    const slots: StorageSlot[] = ['billing', 'shipping', 'postnord', 'nshift', OPT_IN_SLOT]
    slots.forEach((slot) => windowLocalStorageSsr.removeItem(buildKey(identity, slot)))
  } catch (_e) {
    void 0
  }
}

/**
 * Delete entries written by an older schema.
 *
 * The version lives in the key, so a bumped `VERSION` already makes old entries
 * unreachable — but unreachable is not the same as gone, and leaving a shopper's address
 * sitting in their browser forever because we changed a type is not acceptable.
 */
const sweepStaleVersions = (): void => {
  try {
    listBucketKeys()
      .filter((key) => versionOfKey(key) !== `v${VERSION}`)
      .forEach((key) => windowLocalStorageSsr.removeItem(key))
  } catch (_e) {
    void 0
  }
}

/** Drop every remembered address and opt-in on this device, for every identity. */
export const clearRememberedAddresses = (): void => {
  try {
    listBucketKeys().forEach((key) => windowLocalStorageSsr.removeItem(key))
    windowLocalStorageSsr.removeItem(IDENTITY_MARKER_KEY)
  } catch (_e) {
    void 0
  }
}

/**
 * Reconcile the device's buckets with whoever is signed in now, and record them as the
 * current owner. Must run before any read.
 *
 * A browser is not a person. When the previous owner was a signed-in customer, every
 * non-guest bucket here is theirs — logging out, or a second person signing in, must not
 * leave their address behind for whoever sits down next. The guest bucket is kept: it
 * belongs to the un-signed-in state, which the shopper returns to on logout.
 *
 * Signing in from guest purges nothing and merges nothing. The guest bucket may well
 * have been typed by somebody else on this machine, so it is never adopted into an
 * account.
 */
export const reconcileRememberedAddressIdentity = (identity: string): void => {
  try {
    const previous = windowLocalStorageSsr.getItem(IDENTITY_MARKER_KEY)

    if (previous === identity) {
      return
    }

    if (previous && previous !== GUEST_IDENTITY) {
      listBucketKeys()
        .filter((key) => identityOfKey(key) !== GUEST_IDENTITY)
        .forEach((key) => windowLocalStorageSsr.removeItem(key))
    }

    windowLocalStorageSsr.setItem(IDENTITY_MARKER_KEY, identity)
    sweepStaleVersions()
  } catch (_e) {
    void 0
  }
}
